import { z } from 'zod'

const STORAGE_KEY = 'real-maquinas:device-preferences:v1'

const devicePreferencesSchema = z
  .object({
    defaultTechnicianId: z.uuid().optional(),
    visualOrientation: z.enum(['auto', 'portrait', 'landscape']),
  })
  .strict()

export type VisualOrientation = z.infer<typeof devicePreferencesSchema>['visualOrientation']
export type DevicePreferences = z.infer<typeof devicePreferencesSchema>

const defaultPreferences: DevicePreferences = {
  visualOrientation: 'auto',
}

function hasBrowserStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

export function applyVisualOrientation(preferences: DevicePreferences) {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.visualOrientation = preferences.visualOrientation
}

export class DevicePreferencesService {
  get(): DevicePreferences {
    if (!hasBrowserStorage()) return defaultPreferences

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return defaultPreferences
      return devicePreferencesSchema.parse(JSON.parse(raw))
    } catch {
      return defaultPreferences
    }
  }

  save(input: DevicePreferences): DevicePreferences {
    const preferences = devicePreferencesSchema.parse(input)
    if (hasBrowserStorage()) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    }
    applyVisualOrientation(preferences)
    return preferences
  }
}

export function initializeDevicePreferences() {
  const preferences = new DevicePreferencesService().get()
  applyVisualOrientation(preferences)
  return preferences
}
